import { useState } from "react";

interface PostJobModalProps {
  onClose: () => void;
  onPosted: (job: {
    id: string;
    title: string;
    company: string;
    companyDesc: string;
    logo: string;
    coverImage: string;
    location: string;
    type: string;
    salary: string;
    category: string;
    description: string;
    requirements: string[];
    benefits: string[];
    posted: string;
    verified: boolean;
    urgent: boolean;
    applicants: number;
    views: number;
  }) => void;
}

const categories = ["Manufacturing", "Sales", "Marketing", "Logistics", "Creative", "Customer Service"];
const jobTypes = ["Full-time", "Part-time", "Contract", "Internship"];

export default function PostJobModal({ onClose, onPosted }: PostJobModalProps) {
  const [form, setForm] = useState({
    title: "",
    location: "",
    type: "Full-time",
    salary: "",
    category: "Manufacturing",
    description: "",
    requirements: "",
    benefits: "",
    urgent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handlePost = () => {
    if (!form.title || !form.location || !form.salary || !form.description) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setDone(true);
      setTimeout(() => {
        onPosted({
          id: `new-${Date.now()}`,
          title: form.title,
          company: "Your Company",
          companyDesc: "Verified employer on StarBookPrime.",
          logo: "https://public.readdy.ai/ai/img_res/91ad3d67e76f7b46939e3f652f6793aa.jpg",
          coverImage: "https://readdy.ai/api/search-image?query=modern%20USA%20workplace%20office%20factory%20bright%20professional%20team%20working%20together&width=800&height=300&seq=newjob-cover&orientation=landscape",
          location: form.location,
          type: form.type,
          salary: form.salary,
          category: form.category,
          description: form.description,
          requirements: form.requirements.split("\n").filter(Boolean),
          benefits: form.benefits.split("\n").filter(Boolean),
          posted: "Just now",
          verified: true,
          urgent: form.urgent,
          applicants: 0,
          views: 0,
        });
      }, 1000);
    }, 1800);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-orange-100 rounded-xl flex-shrink-0">
              <i className="ri-briefcase-4-line text-orange-500 text-lg" />
            </div>
            <div>
              <h2 className="text-base font-extrabold text-gray-900">Post a New Job</h2>
              <div className="flex items-center gap-1.5 mt-0.5">
                <i className="ri-shield-check-fill text-green-500 text-xs" />
                <p className="text-xs text-green-600 font-semibold">Verified Employer · Your listing will show a verified badge</p>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
            <i className="ri-close-line text-gray-600" />
          </button>
        </div>

        {done ? (
          <div className="p-12 text-center">
            <div className="w-24 h-24 flex items-center justify-center bg-orange-100 rounded-full mx-auto mb-5">
              <i className="ri-briefcase-4-line text-orange-500 text-5xl" />
            </div>
            <h3 className="text-2xl font-extrabold text-gray-900 mb-2">Job Posted!</h3>
            <p className="text-sm text-gray-500 mb-1">
              <strong>{form.title}</strong> is now live with your verified employer badge.
            </p>
            <p className="text-xs text-gray-400 mb-6">Young USA citizens can now find and apply to your job for free.</p>
            <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-xs font-bold px-4 py-2 rounded-full">
              <i className="ri-shield-check-fill" /> Live · Verified Employer Badge Active
            </div>
          </div>
        ) : (
          <>
            <div className="p-6 space-y-5">
              {/* Free apply notice */}
              <div className="bg-green-50 border border-green-100 rounded-xl p-3 flex items-center gap-3">
                <i className="ri-gift-line text-green-500 text-lg flex-shrink-0" />
                <p className="text-xs text-green-700 font-medium">
                  <strong>Reminder:</strong> All USA citizens can apply to your job and send their resume for free. You only pay $5/month to post.
                </p>
              </div>

              {/* Job Title */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Job Title *</label>
                <input
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-400 transition-colors"
                  placeholder="e.g. Warehouse Associate, Sales Rep, Content Creator..."
                />
              </div>

              {/* Location + Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Location *</label>
                  <input
                    value={form.location}
                    onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-400 transition-colors"
                    placeholder="e.g. Los Angeles, CA"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Job Type *</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-400 transition-colors cursor-pointer bg-white"
                  >
                    {jobTypes.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              {/* Salary + Category */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Salary / Pay Rate *</label>
                  <input
                    value={form.salary}
                    onChange={(e) => setForm((f) => ({ ...f, salary: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-400 transition-colors"
                    placeholder="e.g. $18-$22/hr or $50K-$65K/yr"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Category *</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-400 transition-colors cursor-pointer bg-white"
                  >
                    {categories.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Job Description *</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={5}
                  maxLength={500}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-400 transition-colors resize-none"
                  placeholder="Describe the role, daily responsibilities, and what makes this a great opportunity for young workers..."
                />
                <p className="text-xs text-gray-400 mt-1 text-right">{form.description.length}/500</p>
              </div>

              {/* Requirements */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Requirements <span className="font-normal text-gray-400">(one per line)</span></label>
                <textarea
                  value={form.requirements}
                  onChange={(e) => setForm((f) => ({ ...f, requirements: e.target.value }))}
                  rows={4}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-400 transition-colors resize-none"
                  placeholder={"Must be a USA citizen or authorized to work\nNo experience required — we will train you\nMust be 18 or older"}
                />
              </div>

              {/* Benefits */}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Benefits &amp; Perks <span className="font-normal text-gray-400">(one per line)</span></label>
                <textarea
                  value={form.benefits}
                  onChange={(e) => setForm((f) => ({ ...f, benefits: e.target.value }))}
                  rows={3}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-400 transition-colors resize-none"
                  placeholder={"Health Insurance\nPaid Training\nOvertime Pay"}
                />
              </div>

              {/* Urgent toggle */}
              <label className="flex items-center gap-3 cursor-pointer">
                <div
                  onClick={() => setForm((f) => ({ ...f, urgent: !f.urgent }))}
                  className={`w-11 h-6 rounded-full transition-colors flex-shrink-0 relative cursor-pointer ${form.urgent ? "bg-red-500" : "bg-gray-200"}`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${form.urgent ? "translate-x-5" : "translate-x-0.5"}`} />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800">Mark as Urgent Hire</p>
                  <p className="text-xs text-gray-400">Shows a red &quot;Urgent&quot; badge — attracts more applicants fast</p>
                </div>
              </label>

              {/* Policy reminder */}
              <div className="bg-orange-50 border border-orange-100 rounded-xl p-4">
                <p className="text-xs font-extrabold text-orange-800 mb-2"><i className="ri-information-line mr-1" />Job Posting Policy</p>
                <ul className="space-y-1">
                  {[
                    "All jobs must be real, legal, and safe for young USA citizens",
                    "Salary must be accurate — no misleading pay ranges",
                    "No discriminatory requirements based on race, gender, or religion",
                    "Fake or misleading listings will result in immediate account ban",
                  ].map((rule) => (
                    <li key={rule} className="flex items-start gap-2 text-xs text-orange-700">
                      <i className="ri-check-line text-orange-500 flex-shrink-0 mt-0.5" />{rule}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3 sticky bottom-0 bg-white">
              <button onClick={onClose} className="px-6 py-3 border border-gray-200 text-gray-600 text-sm font-semibold rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                Cancel
              </button>
              <button
                onClick={handlePost}
                disabled={submitting || !form.title || !form.location || !form.salary || !form.description}
                className="flex-1 py-3 bg-orange-500 text-white text-sm font-bold rounded-xl hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <><i className="ri-loader-4-line animate-spin" /> Publishing...</>
                ) : (
                  <><i className="ri-send-plane-line" /> Publish Job Listing</>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
